import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { GroupsGuard, RoleGuard } from './role.guard';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { environment as env, environment } from '@env';
import { getAnalytics, provideAnalytics, ScreenTrackingService } from '@angular/fire/analytics';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { PerformanceMonitoringService } from '@angular/fire/compat/performance';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { API_BASE_URL } from '@services/api/api.service';
import { API_NOTIFICATION_BASE_URL } from '@services/api/notification-api.service';
import { API_AGRIROUTER_BASE_URL } from '@services/api/sdf-agrirouter-api.service';
import { ActionReducer, provideStore } from '@ngrx/store';
import { rootReducer, State } from '@redux/index';
import { State as TractorState, tractorReducer } from '@redux/modules/tractorDetail';
import { State as TractorCustomerState, tractorReducer as tractorCustomerReducer } from '@redux/modules/tractorDetailCustomer';
import { geoviewReducer, State as GeoviewState } from '@redux/modules/geoview';
import { geoviewCustomerReducer, State as GeoviewCustomerState } from '@redux/modules/geoview-customer';
import { geoviewDealerReducer, State as GeoviewDealerState } from '@redux/modules/geoview-dealer';
import { State as UserDashboardState, userDashboardReducer } from '@redux/modules/userDashboard';
import { sbdReducer, State as SbdState } from '@redux/modules/sbd';
import { State as UserTractorsState, userTractorsReducer } from '@redux/modules/userTractors';
import { maintenanceReducer, State as MaintenanceState } from '@redux/modules/maintenance';
import { State as VehiclesAlarmsState, vehicleAlarmsReducer } from '@redux/modules/vehicleAlarms';
import { alarmsReducer, State as AlarmsState } from '@redux/modules/alarms';
import { dTechRemoteReducer, State as DTechRemoteState } from '@redux/modules/remote-services/dtech-remote';
import { remoteSupportReducer, State as RemoteSupportState } from '@redux/modules/remote-services/remote-support';
import { connectionsReducer, State as ConnectionsState } from '@redux/modules/connections';
import { storeLogger } from 'ngrx-store-logger';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideEffects } from '@ngrx/effects';
import { CommonEffects } from '@redux/effects';
import { TractorDetailEffects } from '@redux/modules/tractorDetail/effects';
import { TractorDetailCustomerEffects } from '@redux/modules/tractorDetailCustomer/effects';
import { LicenceEffects } from '@redux/modules/licences/effects';
import { MaintenanceEffects } from '@redux/modules/maintenance/effects';
import { GeoviewEffects } from '@redux/modules/geoview/effects';
import { GeoviewCustomerEffects } from '@redux/modules/geoview-customer/effects';
import { GeoviewDealerEffects } from '@redux/modules/geoview-dealer/effects';
import { SbdEffects } from '@redux/modules/sbd/effects';
import { UserDashboardEffects } from '@redux/modules/userDashboard/effects';
import { UserTractorsEffects } from '@redux/modules/userTractors/effects';
import { VehicleAlarmsEffectsEffects } from '@redux/modules/vehicleAlarms/effects';
import { ActiveAlarmEffects } from '@redux/modules/alarms/effects';
import { ConnectionsEffects } from '@redux/modules/connections/effects';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { DateFnsModule } from 'ngx-date-fns';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { provideAnimations } from '@angular/platform-browser/animations';
import { API_DTECH_REMOTE_BASE_URL } from '@services/api/sdf-dtech-remote-api.service';
import { API_DTECH_REMOTE_BASE_URL as API_REMOTE_SUPPORT_BASE_URL } from '@services/sdf-imonitor-remote-api.service';
import { API_CTM_CONFIGURATION_BASE_URL } from '@services/api/sdf-ctm-configuration-management-api.service';
import { NotificationsEffects } from '@redux/modules/notifications/effects';
import { notificationsReducer, State as NotificationsState } from '@redux/modules/notifications';
import { DTechRemoteEffects } from '@redux/modules/remote-services/dtech-remote/effects';
import { RemoteSupportEffects } from '@redux/modules/remote-services/remote-support/effects';
import { CtmMgmtEffects } from '@redux/modules/ctm-mgmt/effects';
import { ctmMgmtReducer, State as CtmMgmtState } from '@redux/modules/ctm-mgmt';
import { licencesReducer, State as LicencesState } from '@redux/modules/licences';
import { API_REMOTE_SERVICES_BASE_URL } from '@services/api/sdf-remote-services-api.service';

export function logger(reducer: ActionReducer<{
  root: State, tractorDetail: TractorState,
  tractorDetailCustomer: TractorCustomerState,
  maintenance: MaintenanceState,
  geoview: GeoviewState,
  geoviewCustomer: GeoviewCustomerState,
  geoviewDealer: GeoviewDealerState,
  userDashboard: UserDashboardState,
  sbd: SbdState,
  alarm: AlarmsState,
  licence: LicencesState,
  notifications: NotificationsState,
  userTractors: UserTractorsState,
  vehiclesAlarms: VehiclesAlarmsState,
  dTechRemote: DTechRemoteState,
  remoteSupport: RemoteSupportState,
  connections: ConnectionsState,
  ctmMgmt: CtmMgmtState,
}>): any {
  // default, no options
  return storeLogger()(reducer);
}

export const metaReducers = environment.production ? [] : [logger];
export const appConfig: ApplicationConfig = {

  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptors([authHttpInterceptorFn])),
    provideAnimations(),
    provideStore({
      root: rootReducer,
      tractorDetail: tractorReducer,
      tractorDetailCustomer: tractorCustomerReducer,
      alarm: alarmsReducer,
      licence: licencesReducer,
      geoview: geoviewReducer,
      geoviewCustomer: geoviewCustomerReducer,
      geoviewDealer: geoviewDealerReducer,
      userDashboard: userDashboardReducer,
      sbd: sbdReducer,
      userTractors: userTractorsReducer,
      notifications: notificationsReducer,
      maintenance: maintenanceReducer,
      vehiclesAlarms: vehicleAlarmsReducer,
      dTechRemote: dTechRemoteReducer,
      remoteSupport: remoteSupportReducer,
      connections: connectionsReducer,
      ctmMgmt: ctmMgmtReducer,
    }, { metaReducers }),
    importProvidersFrom(
      DateFnsModule.forRoot(),
      AngularSvgIconModule.forRoot(),
    ),
    provideAuth0({
      ...env.auth as any,
      httpInterceptor: {
        ...env.httpInterceptor as any,
      },
      errorPath: '/error/declined',
    }),
    provideEffects([
      CommonEffects,
      TractorDetailEffects,
      TractorDetailCustomerEffects,
      LicenceEffects,
      MaintenanceEffects,
      GeoviewEffects,
      GeoviewCustomerEffects,
      GeoviewDealerEffects,
      SbdEffects,
      UserDashboardEffects,
      NotificationsEffects,
      UserTractorsEffects,
      VehicleAlarmsEffectsEffects,
      ActiveAlarmEffects,
      DTechRemoteEffects,
      RemoteSupportEffects,
      ConnectionsEffects,
      CtmMgmtEffects
    ]),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    RoleGuard,
    GroupsGuard,
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAnalytics(() => getAnalytics()),
    provideMessaging(() => getMessaging()),
    providePerformance(() => getPerformance()),
    ScreenTrackingService,
    PerformanceMonitoringService,
    {
      provide: API_BASE_URL,
      useFactory: getBaseUrl,
    },
    {
      provide: API_NOTIFICATION_BASE_URL,
      useFactory: getNotificationApiUrl,
    },
    {
      provide: API_AGRIROUTER_BASE_URL,
      useFactory: getAgrirouterApiUrl,
    },
    {
      provide: API_DTECH_REMOTE_BASE_URL,
      useFactory: getDTechRemoteApiUrl,
    },
    {
      provide: API_REMOTE_SUPPORT_BASE_URL,
      useFactory: getRemoteSupportApiUrl,
    },
    {
      provide: API_REMOTE_SERVICES_BASE_URL,
      useFactory: getRemoteServicesApiUrl,
    },
    {
      provide: API_CTM_CONFIGURATION_BASE_URL,
      useFactory: getCtmConfigurationApiUrl,
    },
  ],
};

// AOT compilation support
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function getBaseUrl(): string {
  return environment.apiURLV2;
}

export function getNotificationApiUrl(): string {
  return environment.notificationApiURL;
}

export function getAgrirouterApiUrl(): string {
  return environment.agrirouterApiURLV2;
}

export function getDTechRemoteApiUrl(): string {
  return environment.dtechRemoteApiURL;
}

export function getRemoteSupportApiUrl(): string {
  return environment.remoteSupportApiURL;
}

export function getRemoteServicesApiUrl(): string {
  return environment.remoteServicesApiURL;
}

export function getCtmConfigurationApiUrl(): string {
  return environment.ctmConfigurationApiURL;
}
